import type { ReactElement } from 'react'
/**
 * CollapseIndicator component for Infrastructure Map
 *
 * Shows a +/- indicator at parent connection points when hovered.
 * Click toggles collapse state for one level.
 * Shift+click triggers recursive collapse/expand with confirmation.
 */

import { useTranslation } from 'react-i18next'
import type { CanvasPosition } from '../../../types/infrastructure-map'

interface CollapseIndicatorProps {
  /** Position where indicator appears (parent's connection point) */
  position: CanvasPosition
  /** Whether the node is currently collapsed */
  isCollapsed: boolean
  /** Number of children that would be collapsed/expanded */
  childCount: number
  /** Whether the indicator is visible (on hover) */
  isVisible: boolean
  /** Click handler (single-click for one level) */
  onClick: () => void
  /** Shift+click handler (for all levels) */
  onShiftClick: () => void
}

// Indicator styling
const INDICATOR_SIZE = 20
const INDICATOR_OFFSET = 0 // Centered on the connection point
const COLLAPSE_INDICATOR_COLOR = 'rgb(217, 70, 239)' // fuchsia-500
const EXPAND_INDICATOR_COLOR = 'rgb(34, 197, 94)' // green-500

export function CollapseIndicator({
  position,
  isCollapsed,
  childCount,
  isVisible,
  onClick,
  onShiftClick,
}: CollapseIndicatorProps): ReactElement | null {
  const { t } = useTranslation('cases')

  // Don't render if not visible or no children
  if (!isVisible || childCount === 0) {
    return null
  }

  const handleClick = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()

    if (e.shiftKey) {
      onShiftClick()
    } else {
      onClick()
    }
  }

  const indicatorColor = isCollapsed ? EXPAND_INDICATOR_COLOR : COLLAPSE_INDICATOR_COLOR
  const symbol = isCollapsed ? '+' : '\u2212'
  const tooltip = isCollapsed
    ? t('infrastructure.canvas.expandChildren', { count: childCount })
    : t('infrastructure.canvas.collapseChildren', { count: childCount })

  return (
    <g
      className="collapse-indicator cursor-pointer"
      onClick={handleClick}
      style={{ pointerEvents: 'auto' }}
    >
      {/* Background circle */}
      <circle
        cx={position.x}
        cy={position.y + INDICATOR_OFFSET}
        r={INDICATOR_SIZE / 2}
        fill={indicatorColor}
        stroke="white"
        strokeWidth={2}
        className="transition-all duration-150 hover:scale-110"
        style={{
          transformOrigin: `${position.x.toString()}px ${(position.y + INDICATOR_OFFSET).toString()}px`,
        }}
      />

      {/* +/- symbol */}
      <text
        x={position.x}
        y={position.y + INDICATOR_OFFSET}
        textAnchor="middle"
        dominantBaseline="central"
        fill="white"
        fontSize={14}
        fontWeight="bold"
        className="pointer-events-none select-none"
        style={{ userSelect: 'none' }}
      >
        {symbol}
      </text>

      {/* Invisible larger hit area for easier clicking */}
      <circle
        cx={position.x}
        cy={position.y + INDICATOR_OFFSET}
        r={INDICATOR_SIZE}
        fill="transparent"
        className="hover:cursor-pointer"
      >
        <title>{tooltip}</title>
      </circle>
    </g>
  )
}

export default CollapseIndicator
